import {
  VenueHours,
  InvertedVenueHours,
  VenueHoursStatus,
  InvertedVenueHoursStatus,
  BarePlan,
  Plan,
} from '../types/plan';
import { minutesToTime, DAY_LENGTH, timeToMinutes } from './time';
import { RangeList } from './rangeList';
import { Time } from '../types/time';
import { hashCode } from './utils';
import { GO_PUBLISHER_ROLE } from '../types/info';

type AnyVenueHoursStatus = VenueHoursStatus | InvertedVenueHoursStatus;

export function invertVenueHours(
  venueHours: VenueHours[],
  dayStartMinutes: number
): InvertedVenueHours[] {
  // Special case: there are no hours. Return a unique block of free time
  if (venueHours.length === 0) {
    return [
      {
        startTime: minutesToTime(dayStartMinutes),
        endTime: minutesToTime(dayStartMinutes),
        status: 'free',
      },
    ];
  }

  // Regular case: do the inverse of the venue hour blocks

  // If there's some venue data already, create a range list and then
  // just pick the free / open ranges (since default is closed)
  // NOTE the rangeList is 0-based
  let rl = new RangeList<AnyVenueHoursStatus>(0, DAY_LENGTH, 'free');

  venueHours.forEach(block => {
    let startMinutes = timeToMinutes(block.startTime, dayStartMinutes);
    let endMinutes = timeToMinutes(block.endTime, dayStartMinutes);

    // Adjust when a period ends at dayStartMinutes (should be end of day)
    if (endMinutes === 0) {
      endMinutes = DAY_LENGTH;
    }

    rl.add(startMinutes, endMinutes, block.status);
  });

  // Now, only push the ranges that are not closed (since those are implicit)
  let inverted: InvertedVenueHours[] = [];

  rl.toArray().forEach(range => {
    if (range.value !== 'closed') {
      inverted.push({
        startTime: minutesToTime(range.start + dayStartMinutes),
        endTime: minutesToTime(range.end + dayStartMinutes),
        status: range.value,
      });
    }
  });

  return inverted;
}

// Checks to see if there is a venue hour that is closed such that a given time intersects
// this venue hour time frame. Returns true if there is such a case (conflict).
export function hasVenueHoursConflict(
  venueHours: VenueHours[],
  startTime?: Time,
  endTime?: Time,
  dayStartMinutes?: number
) {
  if (!startTime || !endTime) {
    return true; // assume conflict if no start time or end time provided
  }

  let startMinutes = timeToMinutes(startTime, dayStartMinutes);
  let endMinutes = timeToMinutes(endTime, dayStartMinutes);
  if (endMinutes === 0) {
    endMinutes = DAY_LENGTH;
  }

  let hasConflict = false;

  venueHours.find(vh => {
    let vhStartMinutes = timeToMinutes(vh.startTime, dayStartMinutes);
    let vhEndMinutes = timeToMinutes(vh.endTime, dayStartMinutes);
    if (vhEndMinutes === 0) {
      vhEndMinutes = DAY_LENGTH;
    }

    if (startMinutes < vhEndMinutes && vhStartMinutes < endMinutes) {
      hasConflict = true;
    }
  });

  return hasConflict;
}

/**
 * Perform the opposite of `invertVenueHours` and then convert the hours array
 * to a format that is understood by the API
 *
 */
export function invertedVenueHoursToApiFormat(
  invertedVenueHours: InvertedVenueHours[],
  dayStartMinutes: number
) {
  // First, we create a rangelist where the default is closed
  let rl = new RangeList<AnyVenueHoursStatus>(0, DAY_LENGTH, 'closed');

  // We add all the entries there (they should all be either open or free)
  invertedVenueHours.forEach(invertedBlock => {
    let startMinutes = timeToMinutes(invertedBlock.startTime, dayStartMinutes);
    let endMinutes = timeToMinutes(invertedBlock.endTime, dayStartMinutes);

    // Adjust when a period ends at dayStartMinutes (should be end of day)
    if (endMinutes === 0) {
      endMinutes = DAY_LENGTH;
    }

    rl.add(startMinutes, endMinutes, invertedBlock.status);
  });

  // Then we get everything except the 'free' blocks
  let venueHours: VenueHours[] = [];
  rl.toArray().forEach(range => {
    if (range.value !== 'free') {
      venueHours.push({
        startTime: minutesToTime(range.start + dayStartMinutes),
        endTime: minutesToTime(range.end + dayStartMinutes),
        status: range.value,
      });
    }
  });

  return venueHours;
}

export function isDayPublished(plan?: BarePlan | Plan) {
  if (!plan) return true;

  return (
    hashCode(JSON.stringify(plan.events)) ===
      hashCode(JSON.stringify(plan.published?.events ?? [])) &&
    hashCode(JSON.stringify(plan.venueData)) ===
      hashCode(JSON.stringify(plan.published?.venueData ?? [])) &&
    hashCode(JSON.stringify(plan.content)) ===
      hashCode(JSON.stringify(plan.published?.content ?? []))
  );
}

export function getPlanPublishedData(plan: BarePlan | Plan, publisherRole: GO_PUBLISHER_ROLE) {
  const events = publisherRole !== 'inactive' ? plan.published?.events ?? [] : plan.events;
  const content = publisherRole !== 'inactive' ? plan.published?.content ?? [] : plan.content;
  const venueData = publisherRole !== 'inactive' ? plan.published?.venueData ?? [] : plan.venueData;

  return {
    events,
    content,
    venueData,
  };
}
